import React from "react";
import { AnimatePresence } from "framer-motion";
import { Info } from "lucide-react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import ItemBulkEditor from "../components/ItemBulkEditor";
import CameraButton from "../components/CameraButton";
import GuestIdentityModal from "../components/GuestIdentityModal";
import InputField from "../components/inputs/InputField";
import ItemList from "../features/ItemList";
import TipTaxTotal from "../features/TipTaxTotal";
import UserSplitTotal from "../features/UserSplitTotal";
import FixedBottomBar from "../features/FixedBottomBar";
import UserBar from "../components/UserBar";
import SelectedUserEditor from "../components/SelectedUserEditor";
import LegendModal from "../components/LegendModal";
import MatesTable from "../components/MatesTable";
import { useReceiptUpload } from "../hooks/useReceiptUpload";
import { useReceiptSync } from "../hooks/useReceiptSync";
import { useUnclaimed } from "../hooks/useSplitCalc";
import { useReceipt } from "../data/ReceiptContext";
import { useAuth } from "../data/AuthContext";

// One page for the whole lifecycle:
//  - "/"           new receipt: edit everything, then share
//  - "/:id" owner  edit everything except items (locked once shared)
//  - "/:id" guest  read-only: claim your identity and pay back
const ReceiptPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { uid } = useAuth();
  const { updateReceipt, setPictureUploaded } = useReceipt();

  const {
    title,
    setTitle,
    items,
    setItems,
    users,
    setUsers,
    selectedUserIndex,
    setSelectedUserIndex,
    whichUserPaidIndex,
    setWhichUserPaidIndex,
    tip,
    setTip,
    tax,
    setTax,
    loadingReceipt,
    me,
    setMe,
    selectedUser,
    canEdit,
    canEditItems,
    canEditSplits,
    canEditSelectedShares,
    share,
    handleSetShare,
    handleSetSplits,
    handleBulkSetSplits,
    handleClaimAll,
    handleUnclaimAll,
    handleBulkDelete,
    handleAddGuestIdentity,
    handleUserVenmoChange,
  } = useReceiptSync(id, uid);

  const isGuest = !!id && !canEdit;
  // "Claim all" while any portion on the check is still open; once nothing is
  // open and the selected mate holds something, it flips to "Unclaim all"
  const hasOpenPortions = items.some(
    (item) =>
      Object.values(item.shares).reduce((a, b) => a + b, 0) < item.splits,
  );
  const selectedHoldsAny =
    !!selectedUser &&
    items.some((item) => (item.shares[selectedUser.id] ?? 0) > 0);
  const showUnclaimAll = !hasOpenPortions && selectedHoldsAny;
  const handleClaimAllToggle = () => {
    if (!selectedUser) return;
    if (showUnclaimAll) handleUnclaimAll(selectedUser.id);
    else handleClaimAll(selectedUser.id);
  };
  const unclaimed = useUnclaimed(items, tax, tip);

  // multi-select of items for the bulk editor (anyone, on the shared check —
  // the split rows' checkboxes). Kept in sync with
  // existing items so a per-row delete can't leave a dangling id selected.
  const [selectedItems, setSelectedItems] = React.useState<string[]>([]);
  const selectedExisting = React.useMemo(
    () => selectedItems.filter((sid) => items.some((it) => it.id === sid)),
    [selectedItems, items],
  );
  // the checked items' split counts, for the bulk bar's stepper
  const selectedSplits = items
    .filter((it) => selectedExisting.includes(it.id))
    .map((it) => it.splits);
  // a fresh selection per phase: clear it when moving between / and /:id
  React.useEffect(() => {
    setSelectedItems([]);
  }, [id]);
  const toggleItemSelect = (itemId: string) =>
    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((x) => x !== itemId)
        : [...prev, itemId],
    );

  const { loading: receiptLoading, handleImageChange } = useReceiptUpload({
    onSuccess: (result) => {
      if (result.title) setTitle(result.title);
      if (result.items) setItems(result.items);
      if (result.tax) setTax(result.tax);
      if (result.tip) setTip(result.tip);
    },
    setPictureUploaded,
    updateReceipt,
  });

  // ?user=<id> deep link (guests): reflect who you are in the URL, and honor an
  // incoming one on load
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUserParamRef = React.useRef(searchParams.get("user"));
  const identitySyncReadyRef = React.useRef(false);
  // the "who are you?" prompt shown to a guest who hasn't picked who they are
  const [showIdentity, setShowIdentity] = React.useState(false);
  // how-to for the item rows (claim box, caret, donut colors)
  const [showLegend, setShowLegend] = React.useState(false);

  const writeUserParam = (userId: string | null) => {
    const next = new URLSearchParams(window.location.search);
    if (userId) next.set("user", userId);
    else next.delete("user");
    setSearchParams(next, { replace: true });
  };

  React.useEffect(() => {
    if (loadingReceipt || identitySyncReadyRef.current) return;
    identitySyncReadyRef.current = true;
    if (!isGuest) return;
    const target = initialUserParamRef.current;
    if (target && users.some((u) => u.id === target)) {
      if (me !== target) setMe(target);
    } else {
      // no valid deep-link identity — clear a stale param and prompt to pick one
      if (target) writeUserParam(null);
      if (!me) setShowIdentity(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingReceipt]);

  React.useEffect(() => {
    if (!identitySyncReadyRef.current || !isGuest) return;
    writeUserParam(me);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  // publish + move the URL to /:id (same route, so no remount) + return the link
  const handleCreateShare = async (): Promise<string | null> => {
    const newId = await share();
    if (!newId) return null;
    navigate(`/${newId}`);
    return `${window.location.origin}/${newId}`;
  };

  if (loadingReceipt) {
    return (
      <div id="root">
        <div
          className="main-content"
          style={{ alignItems: "center", justifyContent: "center" }}
        >
          <ClipLoader size={40} />
        </div>
      </div>
    );
  }

  return (
    <div id="root">
      <div className="main-content">
        <InputField
          value={title}
          setValue={setTitle}
          style={{ textAlign: "center", fontSize: "36px" }}
          editable={canEdit}
        />

        {!!id && (
          <>
            <div className="split-step">
              <div className="step-title">1. Select who you are</div>
              <UserBar
                users={users}
                selectedUserIndex={selectedUserIndex}
                setSelectedUserIndex={setSelectedUserIndex}
                meId={me}
                identityEnabled={!canEdit}
                onSetMe={setMe}
              />
              {/* the selected mate's name + venmo: the owner on anyone, a
                  guest on just their own venmo. No delete once shared. */}
              <SelectedUserEditor
                users={users}
                setUsers={setUsers}
                selectedUserIndex={selectedUserIndex}
                whichUserPaidIndex={whichUserPaidIndex}
                setWhichUserPaidIndex={setWhichUserPaidIndex}
                canEdit={canEdit}
                meId={me}
                onGuestVenmoChange={handleUserVenmoChange}
              />
            </div>
            <div className="split-step">
              <div className="step-title">2. Claim your items and splits</div>
            </div>
          </>
        )}

        {canEditItems && (
          <>
            <div className="center-text">
              The best way to split the check with your mates
            </div>
            <hr className="section-divider" />
            {/* the roster is built here, on the page, before sharing */}
            <MatesTable
              users={users}
              setUsers={setUsers}
              whichUserPaidIndex={whichUserPaidIndex}
              setWhichUserPaidIndex={setWhichUserPaidIndex}
            />
            <hr className="section-divider" />
            <div className="upload-row">
              <CameraButton onChange={handleImageChange}>
                <span>
                  Upload the <strong>Check</strong>
                </span>
              </CameraButton>
            </div>
          </>
        )}

        {/* the item list, with step 2's Claim all / Legend sitting right
            above it on the shared check */}
        <div className="item-list-section">
          {!!id && (
            <div className="step-actions">
              {/* every open portion -> the selected mate, or (once nothing is
                  open) all of their portions back to open */}
              <button
                type="button"
                className="legend-button"
                disabled={
                  !selectedUser || (!hasOpenPortions && !selectedHoldsAny)
                }
                onClick={handleClaimAllToggle}
              >
                {showUnclaimAll ? "Unclaim all" : "Claim all"}
              </button>
              <button
                type="button"
                className="legend-button"
                onClick={() => setShowLegend(true)}
              >
                <Info size={14} />
                Legend
              </button>
            </div>
          )}
          {receiptLoading ? (
            <div className="item-list-loading">
              <ClipLoader size={40} />
            </div>
          ) : (
            <ItemList
              items={items}
              setItems={setItems}
              selectedUserIndex={selectedUserIndex}
              users={users}
              mode={canEditItems ? "init" : "split"}
              canEditSplits={canEditSplits}
              canEditShares={canEditSelectedShares}
              selectable={canEdit}
              selectedItems={selectedExisting}
              onToggleSelect={toggleItemSelect}
              onSetShare={id ? handleSetShare : undefined}
              onSetSplits={id ? handleSetSplits : undefined}
            />
          )}
        </div>

        {/* the whole check — everyone sees it; read-only once shared */}
        <TipTaxTotal
          items={items}
          tip={tip}
          tax={tax}
          setTip={setTip}
          setTax={setTax}
          // tip and tax lock with the items once the check is shared
          editable={canEditItems}
          // nothing can be claimed until the check is shared; after that the
          // row always shows, even at $0.00
          unclaimed={canEditItems ? undefined : unclaimed}
        />

        {/* guests' full breakdown; Pay lives in the bottom bar's compact
            total, so it's hidden here */}
        {isGuest && (
          <UserSplitTotal
            items={items}
            tip={tip}
            tax={tax}
            selectedUser={selectedUser}
            paidUserVenmo={users[whichUserPaidIndex].venmo}
            paidUserId={users[whichUserPaidIndex]?.id}
            isOwner={canEdit}
            showVenmo={false}
          />
        )}
      </div>

      {/* a contextual bar just above the bottom bar — floats in without
          reflowing the item list. Pre-share it bulk-deletes; post-share it
          bulk-sets splits instead (items are locked from deletion by then). */}
      {selectedExisting.length > 0 && (
        <ItemBulkEditor
          count={selectedExisting.length}
          splits={selectedSplits[0] ?? 1}
          mixed={new Set(selectedSplits).size > 1}
          onSetSplits={
            id ? (n) => handleBulkSetSplits(selectedExisting, n) : undefined
          }
          onDelete={
            canEditItems
              ? () => {
                  handleBulkDelete(selectedExisting);
                  setSelectedItems([]);
                }
              : undefined
          }
          onClear={() => setSelectedItems([])}
        />
      )}

      <FixedBottomBar
        canEdit={canEdit}
        showShareHint={canEditItems}
        summary={
          !canEditItems && (
            <UserSplitTotal
              items={items}
              tip={tip}
              tax={tax}
              selectedUser={selectedUser}
              paidUserVenmo={users[whichUserPaidIndex].venmo}
              paidUserId={users[whichUserPaidIndex]?.id}
              isOwner={canEdit}
              compact
            />
          )
        }
        existingUrl={id ? `${window.location.origin}/${id}` : null}
        onCreate={handleCreateShare}
      />

      <AnimatePresence>
        {showLegend && (
          <LegendModal key="legend" handleClose={() => setShowLegend(false)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showIdentity && isGuest && !me && (
          <GuestIdentityModal
            key="identity"
            users={users}
            onSelectExisting={(userId) => {
              setMe(userId);
              setShowIdentity(false);
            }}
            onCreateNew={(name, venmo) => {
              handleAddGuestIdentity(name, venmo);
              setShowIdentity(false);
            }}
            handleClose={() => setShowIdentity(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReceiptPage;
